import { Component, computed, effect, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { WaitingListAttendantUpsertDialogStore } from './waiting-list-attendant-upsert-dialog.store';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { AppFormGroup, formAddText } from '../../../../utils/form.utils';
import { fb, translate } from '../../../../modules/shared/shared.global-service';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { waitingListRemovalReasonOptions } from '@libs/types/src/services/enum/waiting-list-removal-reason.enum';
import { CheckInputComponent } from '../../../kit/input/check-input/check-input.component';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { wysiwygPatterGroupsFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import { SelectUserComponent } from '../../../../modules/content/components/common/select-user/select-user.component';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { FileUploadBoxComponent } from '../../../kit/file-upload-box/file-upload-box.component';
import { TableColumn } from '../../../../modules/shared/components/kit/table/table.types';
import { dateColumn, numberField } from '../../../../modules/shared/components/kit/table/table.fields';
import {
    prevContractsFrag,
    ratingsFrag,
    updateWaitingListItemFrag,
} from './waiting-list-attendant-upsert-dialog.query';
import { Operator } from '../../../../../../../data-grid/src/grid/grid-request.dto';
import { CardComponent } from '../../../kit/card/card.component';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import { ServiceGroupObject } from '@libs/types/src/services/object-type/service/service-group.object';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import {
    WaitingListRatingUpsertDialogComponent,
} from '../waiting-list-rating-upsert-dialog/waiting-list-rating-upsert-dialog.component';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { WaitingListUpdateInput } from '@libs/types/src/services/input-type/waiting-list-update.input';
import { Validators } from '@angular/forms';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import {
    ServiceFormEnum,
    serviceFormEnumOptions,
} from '../../../../../../../types/src/services/enum/service-form.enum';
import { MultiSelectInputComponent } from '../../../kit/input/multi-select-input/multi-select-input.component';

@Component({
    selector: 'app-waiting-list-attendant-upsert-dialog',
    templateUrl: './waiting-list-attendant-upsert-dialog.component.html',
    styleUrls: ['./waiting-list-attendant-upsert-dialog.component.scss'],
    providers: [WaitingListAttendantUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        SelectInputComponent,
        CheckInputComponent,
        ServicePatternWysiwygComponent,
        SelectUserComponent,
        FileUploadBoxComponent,
        CardComponent,
        CardTableComponent,
        MultiSelectInputComponent,
    ],
})
export class WaitingListAttendantUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(WaitingListAttendantUpsertDialogStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);

    protected readonly filesUpload = viewChild(FileUploadBoxComponent);
    public ratingsTable = viewChild<CardTableComponent>('ratingsTable');

    protected form = new AppFormGroup({
        entryDate: fb.control<Date | null>(null, Validators.required),
        dateTo: fb.control<Date | null>(null),
        removalReason: fb.control<string | null>(null),
        readyToUseService: fb.control<boolean>(false),
        note: fb.control<string | null>(''),
        userId: fb.control<string | null>(null),
        form: fb.control<string[] | null>(null, Validators.required),
        files: fb.control<number[]>([]),
    });
    protected removalReasonChanged = this.form.signalCodeChange<string | null>('removalReason');

    protected prefix = 'waitingList.attendant.';

    protected additionalFilter = computed(() => {
        return [
            [{
                column: 'waitingListId',
                operator: Operator.EQ,
                value: [this.item.id],
            }],
        ];
    });

    protected readonly ratingColumns = computed<TableColumn[]>(() => {
        return [
            dateColumn,
            numberField('totalPoints'),
        ];
    });

    protected item!: WaitingListObject;

    protected serviceFormOptions: any | null = null;

    constructor() {
        super();

        this.store.onSuccess(
            () => this.ratingsTable()?.reload(),
            'createWaitingListRating',
            'updateWaitingListRating',
            'deleteWaitingListRating',
        );

        this.item = this.dialogConfig.data.item;

        const serviceForm = this.store.globalGlobalStore.organizationStore.getServiceById(this.item.folder.serviceId)()?.form || [];
        this.serviceFormOptions = serviceFormEnumOptions.filter(it => serviceForm.includes(it.value as any));

        if (this.item) {
            this.form.patchValue({
                entryDate: new Date(this.item.entryDate),
                dateTo: this.item.dateTo ? new Date(this.item.dateTo) : null,
                removalReason: this.item.removalReason,
                readyToUseService: this.item.readyToUseService,
                note: this.item.note || '',
                form: this.item.form,
                userId: this.item.userId,
                files: this.item.files,
            }, { isCodeUpdate: true });
        }

        effect(() => {
            this.fetchPrevContracts();
        });

        effect(() => {
            const prevContracts = this.store.data.getFolderContracts();

            if (!prevContracts || this.form.value.form) return;

            if (!prevContracts?.['items'].length) {
                const servFormOpts = this.serviceFormOptions;
                if (servFormOpts.length === 1) {
                    this.form.patchValue({ form: [servFormOpts[0].value] });
                }
            } else {
                const contract = prevContracts?.['items'][0];
                if (contract) {
                    const values = [];
                    if (contract.formField) {
                        values.push(ServiceFormEnum.FIELD);
                    }
                    if (contract.formResidental) {
                        values.push(ServiceFormEnum.RESIDENTIAL);
                    }
                    if (contract.formAmbulant) {
                        values.push(ServiceFormEnum.AMBULANT);
                    }
                    this.form.patchValue({ form: values });
                }
            }
        });

        effect(() => {
            const removalReson = this.removalReasonChanged();
            if (removalReson.isCodeUpdate) return;

            if (removalReson.value && !this.form.value.dateTo) {
                this.form.patchValue({ dateTo: new Date() });
            }
        });
    }

    public override async save(_event: Event) {
        if (isInvalid(this.form)) return;

        const newFiles = await this.uploadFiles();

        const _data = {
            id: this.item.id,
            entryDate: this.form.value.entryDate?.toISOString(),
            dateTo: this.form.value.dateTo?.toISOString() || null,
            removalReason: this.form.value.removalReason,
            readyToUseService: this.form.value.readyToUseService,
            note: this.form.value.note,
            form: this.form.value.form,
            userId: this.form.value.userId,
            files: [...(this.form.value.files || []), ...newFiles],
        } as WaitingListUpdateInput;

        await this.store.api.updateWaitingListItem(_data, updateWaitingListItemFrag);

        contentGlobalSuccess.next('common.updated');

        this.dialogRef.close();
    }

    protected openRating(item: ServiceGroupObject | null = null) {
        this.dialogService.open(
            WaitingListRatingUpsertDialogComponent,
            {
                header: translate.instant('waitingList.attendant.ratings.title'),
                styleClass: 'p-dialog-width-30',
                data: {
                    waitingList: this.item,
                    item: item,
                },
            });
    }

    protected async deleteRating(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteWaitingListRating({ id: event.item.id }).then(() => {
                    contentGlobalSuccess.next('common.deleted');
                });
            },
            event,
        );
    }

    private async uploadFiles(): Promise<number[]> {
        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.WAITING_LIST,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [
                {
                    externalType: ExternalType.folder,
                    externalId: String(this.item.folderId),
                },
                {
                    externalType: ExternalType.service,
                    externalId: String(this.item.folder.serviceId),
                },
            ],
        };

        return this.filesUpload()?.uploadFiles(metadata) || [];
    }

    private async fetchPrevContracts() {
        const filter = new FilterBuilder({
            personId: this.item.folder.personId,
            serviceId: this.item.folder.serviceId,
        });

        filter.where('validTo', 'true', Operator.NEMPTY);
        filter.where('validTo', (new Date()).toISOString(), Operator.LT);
        filter.sort('validTo', 'DESC');
        filter.limit(1);

        await this.store.api.getFolderContracts(filter.filter(), prevContractsFrag);
    }

    protected readonly waitingListRemovalReasonOptions = waitingListRemovalReasonOptions;
    protected readonly wysiwygPatterGroupsFolder = wysiwygPatterGroupsFolder;
    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly ratingsFrag = ratingsFrag;
}
